import React, {useMemo} from 'react'
import {Box, Skeleton, Typography} from '@mui/material'
import {alpha, useTheme} from '@mui/material/styles'
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, XAxis, YAxis} from 'recharts'
import {useTranslation} from 'react-i18next'
import {useGetInvoicesDashboardQuery} from 'store/Store'
import SectionCard from 'components/Page/Dashboard/SectionCard'
import DashboardChartTooltip from 'components/Page/Dashboard/DashboardChartTooltip'
import {formatMoney} from 'components/Page/Dashboard/dashboardFormat'

const MONTH_KEYS = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
]

const BillingChart = () => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const {data, isFetching} = useGetInvoicesDashboardQuery()

    const {rows, yearTotal} = useMemo(() => {
        const months = data?.months || []
        return {
            rows: months.map(({month, paid, unpaid}) => ({
                month: t(`months.short.${MONTH_KEYS[month - 1]}`),
                paid,
                unpaid,
            })),
            yearTotal: months.reduce((sum, {total}) => sum + total, 0),
        }
    }, [data, t])

    let content
    if (isFetching) {
        content = <Skeleton variant="rounded" height="100%" />
    } else if (!yearTotal) {
        content = (
            <Box sx={{height: '100%', display: 'grid', placeItems: 'center'}}>
                <Typography variant="body2" color="text.secondary">
                    {t('dashboard.billing.empty')}
                </Typography>
            </Box>
        )
    } else {
        content = (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{top: 8, right: 8, left: 0, bottom: 0}}>
                    <CartesianGrid
                        vertical={false}
                        strokeDasharray="3 3"
                        stroke={alpha(palette.divider, 0.6)}
                    />
                    <XAxis
                        dataKey="month"
                        tickLine={false}
                        axisLine={false}
                        tick={{fontSize: 12, fill: palette.text.secondary}}
                    />
                    <YAxis
                        width={40}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => (value >= 1000 ? `${value / 1000}k` : value)}
                        tick={{fontSize: 12, fill: palette.text.secondary}}
                    />
                    <DashboardChartTooltip
                        cursor={{fill: alpha(palette.primary.main, 0.04)}}
                        formatter={(value, name) => [
                            formatMoney(value),
                            t(`dashboard.billing.${name}`),
                        ]}
                    />
                    <Legend
                        iconType="circle"
                        iconSize={8}
                        formatter={(name) => t(`dashboard.billing.${name}`)}
                        wrapperStyle={{fontSize: 12}}
                    />
                    <Bar dataKey="paid" stackId="billing" fill={palette.success.main} />
                    <Bar
                        dataKey="unpaid"
                        stackId="billing"
                        fill={palette.warning.main}
                        radius={[4, 4, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>
        )
    }

    return (
        <SectionCard
            title={t('dashboard.billing.title')}
            subtitle={t('dashboard.billing.subtitle', {
                year: data?.year,
                total: formatMoney(yearTotal),
            })}>
            <Box sx={{height: 300}}>{content}</Box>
        </SectionCard>
    )
}

export default BillingChart
